import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http'
import { HTTP_INTERCEPTORS } from '@angular/common/http'
import { ApplicationConfig, inject, provideAppInitializer } from '@angular/core'
import {
  provideClientHydration,
  withI18nSupport,
  withNoHttpTransferCache
} from '@angular/platform-browser'
import { provideRouter, withComponentInputBinding, withRouterConfig } from '@angular/router'

import {
  MsalInterceptor,
  MsalService,
  MsalGuard,
  MsalBroadcastService,
  MSAL_INSTANCE,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG
} from '@azure/msal-angular'
import {
  IPublicClientApplication,
  PublicClientApplication,
  BrowserCacheLocation,
  LogLevel,
  InteractionType
} from '@azure/msal-browser'
import { provideTranslateService } from '@ngx-translate/core'
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader'
import { environment } from 'environments/environment'
import { RECAPTCHA_V3_SITE_KEY } from 'ng-recaptcha'
import { MessageService } from 'primeng/api'
import { providePrimeNG } from 'primeng/config'

import { giftCardInterceptor } from 'app/_shared/interceptors/giftCard.interceptor'
import { tokenInterceptor } from 'app/_shared/interceptors/token.interceptor'
import { AuthService } from 'app/_shared/services/auth.service'
import MyPreset from 'app/theme-preset'

import { AppConfigService } from './app-config.service'
import { routes } from './app.routes'

export function MSALInstanceFactory(appConfigService: AppConfigService): IPublicClientApplication {
  const config = appConfigService.getAppConfig
  const sso = config?.sso
  // NOTE: This factory runs for every storefront that reaches bootstrap,
  // including Okta-only tenants. MSAL is tolerant of the re-used `client_id`,
  // but ideally we'd provide MSAL conditionally when `sso.provider === 'microsoft'`.
  // See the `feature/okta-sso` review for context.
  const clientId = sso?.client_id || ''
  const tenantId = sso?.tenant_id || ''

  return new PublicClientApplication({
    auth: {
      clientId,
      authority: `https://login.microsoftonline.com/${tenantId}`,
      redirectUri: window.location.origin,
      navigateToLoginRequestUrl: false
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: false
    },
    system: {
      loggerOptions: {
        loggerCallback: () => {},
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false
      }
    }
  })
}

export function MSALInterceptorConfigFactory() {
  const protectedResourceMap = new Map<string, Array<string>>()
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['user.read'])
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  }
}

export function MSALGuardConfigFactory() {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['user.read']
    }
  }
}

export function initializeApp(appConfigService: AppConfigService): () => Promise<void> {
  return () => appConfigService.Init()
}

export function initializeUser(authService: AuthService): () => Promise<void> {
  return () => authService.getUser()
}

export function redirectLegacyStorefront(): void {
  if (typeof window === 'undefined') {
    return
  }

  if (window.location.hostname === 'newcharter.singleserve.store') {
    window.location.replace(
      `https://new-charter-2026.singleserve.store${window.location.pathname}${window.location.search}${window.location.hash}`
    )
  }
}

function recaptchaKey() {
  return environment.RECAPTCHA_SITE_KEY
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      routes,
      withComponentInputBinding(),
      withRouterConfig({ paramsInheritanceStrategy: 'always' })
    ),
    provideClientHydration(withNoHttpTransferCache(), withI18nSupport()),
    provideHttpClient(withFetch(), withInterceptors([tokenInterceptor, giftCardInterceptor])),
    providePrimeNG({
      theme: {
        preset: MyPreset,
        options: {
          darkModeSelector: '.my-app-dark'
        }
      }
    }),
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: '/assets/i18n/',
        suffix: '.json'
      })
    }),
    provideAppInitializer(redirectLegacyStorefront),
    provideAppInitializer(() => {
      const initializerFn = initializeUser(inject(AuthService))
      return initializerFn()
    }),
    provideAppInitializer(() => {
      const initializerFn = initializeApp(inject(AppConfigService))
      return initializerFn()
    }),
    { provide: RECAPTCHA_V3_SITE_KEY, useValue: recaptchaKey() },
    // MSAL providers
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
      deps: [AppConfigService]
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    MessageService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    }
  ]
}
